import React, {useMemo, useState} from 'react';
import s from './ActivityTreeTable.module.css';
import {UserActivityChildren} from '../../../types/TypesUser';
import {useTranslation} from 'react-i18next';
import {formatNumber} from '../../../assets/utils/utils';

type Props = {
	nodes: UserActivityChildren[];
};

const MAX_LEVEL = 7;

const ActivityTreeTable: React.FC<Props> = ({nodes}) => {
	const {t} = useTranslation();
	const [open, setOpen] = useState<Record<number, boolean>>({});

	const totals = useMemo(() => {
		return nodes.reduce(
			(acc, n) => {
				acc.a5 += Number(n.tree_activity_5level || 0);
				acc.a6 += Number(n.tree_activity_6level || 0);
				acc.a7 += Number(n.tree_activity_7level || 0);
				return acc;
			},
			{a5: 0, a6: 0, a7: 0},
		);
	}, [nodes]);

	const toggle = (id: number) => {
		setOpen((prev) => ({...prev, [id]: !prev[id]}));
	};

	const renderRows = (
		list: UserActivityChildren[] | undefined,
		parentLevel: number,
	): JSX.Element[] | null => {
		if (!list || parentLevel > MAX_LEVEL) return null;
		return list.map((node) => {
			const isOpen = open[node.id];
			const hasLocalChildren =
				Array.isArray(node.children) && node.children.length > 0;
			const canToggle = node.has_children || hasLocalChildren;
			const nextLevel = node.level + 1;
			return (
				<React.Fragment key={node.id}>
					<div className={s.row} data-level={node.level}>
						<div className={s.cellToggle}>
							{canToggle ? (
								<button
									type="button"
									className={s.toggleBtn}
									onClick={() => toggle(node.id)}
									aria-expanded={isOpen}
								>
									<span className={isOpen ? s.arrowOpen : s.arrow} />
								</button>
							) : (
								<span className={s.noArrow} />
							)}
						</div>
						<button
							type="button"
							className={s.cellName}
							onClick={() => canToggle && toggle(node.id)}
						>
							{node.name}
						</button>
						<div className={s.cellLevel}>L{node.level}</div>
						<div className={s.cellA}>
							{formatNumber(Number(node.tree_activity_5level || 0))}
						</div>
						<div className={s.cellA}>
							{formatNumber(Number(node.tree_activity_6level || 0))}
						</div>
						<div className={s.cellA}>
							{formatNumber(Number(node.tree_activity_7level || 0))}
						</div>
					</div>
					{isOpen && hasLocalChildren && (
						<div className={s.children} data-parent-level={node.level}>
							{renderRows(node.children as any, nextLevel)}
						</div>
					)}
				</React.Fragment>
			);
		});
	};

	return (
		<div className={s.container}>
			<div className={s.table}>
				<div className={s.totals}>
					<div />
					<div className={s.tLabel}>{t('Разом')}</div>
					<div />
					<div className={s.cellA}>{formatNumber(totals.a5)}</div>
					<div className={s.cellA}>{formatNumber(totals.a6)}</div>
					<div className={s.cellA}>{formatNumber(totals.a7)}</div>
				</div>
				<div className={s.header}>
					<div className={s.hCellToggle} />
					<div className={s.hCellName}>{t("Ім'я")}</div>
					<div className={s.hCellLevel}>{t('Рівень')}</div>
					<div className={s.hCellA}>{t('Сум')} 5</div>
					<div className={s.hCellA}>{t('Сум')} 6</div>
					<div className={s.hCellA}>{t('Сум')} 7</div>
				</div>
				<div className={s.body}>{renderRows(nodes, 1)}</div>
			</div>
		</div>
	);
};

export default ActivityTreeTable;
